export type DateInput = string | Date | number | null | undefined;

export const parseDateInput = (value: DateInput): Date | null => {
  if (value === null || value === undefined || value === '') return null;

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  if (typeof value !== 'string') return null;

  let normalized = value.trim();
  if (!normalized) return null;

  // Treat common "zero" dates as empty
  if (
    normalized === '00-00-0000' ||
    normalized === '00/00/0000' ||
    normalized === '0000-00-00' ||
    normalized === '0000/00/00'
  ) {
    return null;
  }

  // Handle space-separated dates like "07 11 2025" -> convert to MM-DD-YYYY
  const spaceDateMatch = normalized.match(/^(\d{1,2})\s+(\d{1,2})\s+(\d{4})(?:\s+.*)?$/);
  if (spaceDateMatch && !normalized.includes('-') && !normalized.includes('/')) {
    const month = spaceDateMatch[1].padStart(2, '0');
    const day = spaceDateMatch[2].padStart(2, '0');
    const year = spaceDateMatch[3];
    normalized = `${month}-${day}-${year}`;
  }

  // YYYY-MM-DD or YYYY/MM/DD (optionally with time) -> parse as local date to avoid timezone shifts
  const ymdMatch = normalized.match(/^(\d{4})[-\/](\d{2})[-\/](\d{2})(?:[T\s].*)?$/);
  if (ymdMatch) {
    const year = Number(ymdMatch[1]);
    const month = Number(ymdMatch[2]);
    const day = Number(ymdMatch[3]);
    const containsTime = /[T\s]/.test(normalized) && /:\d{2}/.test(normalized);
    if (containsTime) {
      const dateWithTime = new Date(normalized);
      return isNaN(dateWithTime.getTime()) ? null : dateWithTime;
    }
    return createLocalDate(year, month, day);
  }

  // MM/DD/YYYY or MM-DD-YYYY (optionally with time); fallback to DD/MM/YYYY when unambiguous
  const mdyOrDmyMatch = normalized.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})(?:[T\s].*)?$/);
  if (mdyOrDmyMatch) {
    const part1 = Number(mdyOrDmyMatch[1]);
    const part2 = Number(mdyOrDmyMatch[2]);
    const year = Number(mdyOrDmyMatch[3]);

    const mdy = createLocalDate(year, part1, part2);
    if (mdy) return mdy;

    const dmy = createLocalDate(year, part2, part1);
    if (dmy) return dmy;
  }

  const date = new Date(normalized);
  return isNaN(date.getTime()) ? null : date;
};

export const formatDateFromDate = (date: Date, useUtc = false): string => {
  const month = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    timeZone: useUtc ? 'UTC' : undefined,
  }).format(date);
  const day = String(useUtc ? date.getUTCDate() : date.getDate()).padStart(2, '0');
  const year = useUtc ? date.getUTCFullYear() : date.getFullYear();
  return `${month} ${day} ${year}`;
};

export const formatDateMMDDYYYY = (value: DateInput, fallback = ''): string => {
  const date = parseDateInput(value);
  if (!date) return fallback;
  return formatDateFromDate(date);
};

export const formatDateTimeFromDate = (date: Date, useUtc = false, includeSeconds = false): string => {
  const hours = useUtc ? date.getUTCHours() : date.getHours();
  const minutes = useUtc ? date.getUTCMinutes() : date.getMinutes();
  const seconds = useUtc ? date.getUTCSeconds() : date.getSeconds();
  const hour12 = hours % 12 || 12;
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const minuteStr = String(minutes).padStart(2, '0');
  const time = includeSeconds
    ? `${hour12}:${minuteStr}:${String(seconds).padStart(2, '0')} ${ampm}`
    : `${hour12}:${minuteStr} ${ampm}`;
  return `${formatDateFromDate(date, useUtc)}, ${time}`;
};

interface DateTimeDisplayOptions {
  useUtc?: boolean;
  uppercaseAmPm?: boolean;
  separator?: string;
  fallback?: string;
}

interface RelativeDateTimeDisplayOptions extends DateTimeDisplayOptions {
  relativeDaysThreshold?: number;
}

const formatTime12 = (date: Date, useUtc = false, uppercaseAmPm = false): string => {
  const hours = useUtc ? date.getUTCHours() : date.getHours();
  const minutes = useUtc ? date.getUTCMinutes() : date.getMinutes();
  const hour12 = hours % 12 || 12;
  const minuteStr = String(minutes).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const ampmStr = uppercaseAmPm ? ampm : ampm.toLowerCase();
  return `${hour12}:${minuteStr}${ampmStr}`;
};

export const formatDateTimeForDisplay = (value: DateInput, options: DateTimeDisplayOptions = {}): string => {
  const fallback = options.fallback ?? '-';
  const date = parseDateInput(value);
  if (!date) return fallback;
  const datePart = formatDateFromDate(date, options.useUtc);
  const timePart = formatTime12(date, options.useUtc, options.uppercaseAmPm ?? false);
  const separator = options.separator ?? ' | ';
  return `${datePart}${separator}${timePart}`;
};

export const formatRelativeOrAbsoluteDateTimeForDisplay = (
  value: DateInput,
  options: RelativeDateTimeDisplayOptions = {}
): string => {
  const fallback = options.fallback ?? '-';
  const date = parseDateInput(value);
  if (!date) return fallback;

  const separator = options.separator ?? ' | ';
  const timePart = formatTime12(date, options.useUtc, options.uppercaseAmPm ?? true);
  const threshold = options.relativeDaysThreshold ?? 7;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  if (diffMs >= 0) {
    const diffMinutes = Math.floor(diffMs / (60 * 1000));
    const diffHours = Math.floor(diffMs / (60 * 60 * 1000));
    const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));

    if (diffHours < 24) {
      let label: string;
      if (diffMinutes < 1) {
        label = 'Just now';
      } else if (diffHours < 1) {
        label = diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`;
      } else {
        label = diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
      }
      return label;
    }

    if (diffDays <= threshold) {
      const daysLabel = diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
      return `${daysLabel}${separator}${timePart}`;
    }
  }

  return `${formatDateFromDate(date, options.useUtc)}${separator}${timePart}`;
};

export const formatDateTimeMMDDYYYY = (
  value: DateInput,
  fallback = '',
  useUtc = false,
  includeSeconds = false
): string => {
  const date = parseDateInput(value);
  if (!date) return fallback;
  return formatDateTimeFromDate(date, useUtc, includeSeconds);
};

const createLocalDate = (year: number, month: number, day: number): Date | null => {
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > 31) return null;

  const date = new Date(year, month - 1, day);
  if (isNaN(date.getTime())) return null;

  // Ensure we didn't overflow (e.g. 02/31/2025)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;

  return date;
};
